import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack, Button, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Typography, Alert,
} from '@mui/material'

// Bounded so a long-running audit session (many polls, a full re-sync) can't
// grow this list unboundedly in memory.
const MAX_ENTRIES = 500

let idCounter = 0

function CheckpointTimelinePanel() {
  const { t } = useTranslation()
  const [entries, setEntries] = useState([])

  useEffect(() => {
    const handler = (event) => {
      idCounter += 1
      const detail = event?.detail || {}
      setEntries(prev => [{ id: idCounter, ...detail }, ...prev].slice(0, MAX_ENTRIES))
    }
    window.addEventListener('lobby:sync-checkpoint', handler)
    return () => window.removeEventListener('lobby:sync-checkpoint', handler)
  }, [])

  const clear = useCallback(() => setEntries([]), [])

  const summaryByEntity = useMemo(() => {
    const map = new Map()
    for (const entry of entries) {
      const current = map.get(entry.entity) || { entity: entry.entity, pages: 0, items: 0, failures: 0 }
      current.pages += 1
      current.items += entry.itemsReceived || 0
      if (entry.success === false) current.failures += 1
      map.set(entry.entity, current)
    }
    return [...map.values()]
  }, [entries])

  return (
    <Stack spacing={3} className="audit-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <h3>{t('audit_checkpoint_timeline_title')}</h3>
        <Button variant="outlined" onClick={clear} disabled={entries.length === 0}>
          {t('audit_clear_log')}
        </Button>
      </Stack>

      <Typography className="audit-section-note">{t('audit_checkpoint_timeline_note')}</Typography>

      {summaryByEntity.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {summaryByEntity.map(s => (
            <Chip
              key={s.entity}
              color={s.failures > 0 ? 'error' : 'default'}
              label={`${s.entity}: ${s.pages} ${t('audit_checkpoint_pages')} · ${s.items} ${t('audit_checkpoint_items')}`}
            />
          ))}
        </Stack>
      )}

      {entries.length === 0 && (
        <Alert severity="info">{t('audit_checkpoint_timeline_empty')}</Alert>
      )}

      {entries.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('audit_event_log_time')}</TableCell>
              <TableCell>{t('audit_checkpoint_entity')}</TableCell>
              <TableCell>{t('audit_checkpoint_page')}</TableCell>
              <TableCell>{t('audit_checkpoint_sent')}</TableCell>
              <TableCell align="right">{t('audit_checkpoint_items')}</TableCell>
              <TableCell>{t('audit_checkpoint_has_more')}</TableCell>
              <TableCell>{t('audit_checkpoint_new')}</TableCell>
              <TableCell align="right">{t('audit_checkpoint_duration')}</TableCell>
              <TableCell>{t('audit_store_status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(entry => (
              <TableRow key={entry.id}>
                <TableCell>{new Date(entry.timestamp).toLocaleTimeString()}</TableCell>
                <TableCell>{entry.entity}</TableCell>
                <TableCell>{entry.page}</TableCell>
                <TableCell>{entry.requestCheckpoint}</TableCell>
                <TableCell align="right">{entry.itemsReceived ?? '—'}</TableCell>
                <TableCell>{entry.hasMore === undefined ? '—' : String(entry.hasMore)}</TableCell>
                <TableCell>{entry.responseCheckpoint || '—'}</TableCell>
                <TableCell align="right">{entry.durationMs != null ? `${entry.durationMs}ms` : '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={entry.success === false ? t('audit_fail') : t('audit_pass')}
                    color={entry.success === false ? 'error' : 'success'}
                  />
                  {entry.error && (
                    <Typography className="audit-section-note" component="span" sx={{ ml: 1 }}>
                      {entry.error}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  )
}

export default CheckpointTimelinePanel
